import { Box, Drawer } from '@mui/material';
import React, { useState } from 'react';

import { palette } from '../../../../assets/styles/themes/tokens';
import AiOnlyWelcomeDialog from '../../../../components/AiOnlyWelcomeDialog';
import useHomeNavigation from '../../../../utils/useHomeNavigation';
import { useIsSidebarCollapsed } from '../../../../utils/useIsSidebarCollapsed';
import useScreenSize from '../../../../utils/useScreenSize';
import useShouldShowAIAssistant from '../../../../utils/useShouldShowAIAssistant';
import AIAssistantDialog from '../../../AIAssistant/Dialog/AIAssistantDialog';
import LBReOnboardingDialog from '../../../reOnboardingDialog';
import UpgradeBanner from '../../../UpgradeBanner';
import SideNavbar from '../../SideNavbar';
import TopBar from '../TopBar';

interface NewLayoutProps {
	children: React.ReactNode | React.ReactNode[];
	topBarContent: React.ReactNode;
	mainContentRef: React.RefObject<HTMLElement>;
	sidebarCollapsed: boolean;
	isNewMerklistePage: boolean;
}

const NewLayout = ({
	children,
	topBarContent,
	mainContentRef,
	sidebarCollapsed,
	isNewMerklistePage,
}: NewLayoutProps) => {
	const shouldShowAIAssistant = useShouldShowAIAssistant();
	const shouldCollapseSidebar = useIsSidebarCollapsed();
	const { isMdScreen, isLgScreen } = useScreenSize();
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

	const isDesktopScreen = isMdScreen || isLgScreen;
	const isSidebarCollapsed = sidebarCollapsed || shouldCollapseSidebar;

	const handleCloseDrawer = () => {
		setIsDrawerOpen(false);
	};

	const handleOpenDrawer = () => {
		setIsDrawerOpen(true);
	};

	const handleLogoClick = useHomeNavigation();

	return (
		<>
			<Box
				sx={{
					backgroundColor: isNewMerklistePage ? palette.background.paperElevation1 : 'inherit',
					display: 'grid',
					width: '100%',
					height: '100vh',
					gridTemplateRows: 'auto 1fr',
					gridTemplateAreas: `
        "header"
        "main"
      `,
				}}
				data-testid="master-layout-new-navigation"
			>
				<Box sx={{ gridArea: 'header' }}>
					<UpgradeBanner />
				</Box>
				<Box
					sx={{
						gridArea: 'main',
						overflow: 'hidden',
						width: '100%',
						height: '100%',
						display: 'flex',
						flexDirection: 'row',
					}}
				>
					{isDesktopScreen && <SideNavbar isCollapsed={isSidebarCollapsed} />}
					{!isDesktopScreen && (
						<Drawer open={isDrawerOpen} anchor="right" onClose={handleCloseDrawer}>
							<SideNavbar isCollapsed={false} isDrawer onCloseDrawer={handleCloseDrawer} />
						</Drawer>
					)}
					<LBReOnboardingDialog />
					<AiOnlyWelcomeDialog />
					<Box
						sx={{
							height: '100%',
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden',
						}}
					>
						{topBarContent && (
							<TopBar
								onHomeClick={handleLogoClick}
								onBurgerMenuClick={handleOpenDrawer}
								content={topBarContent}
							/>
						)}
						<Box
							ref={mainContentRef}
							component="main"
							sx={{
								height: '100%',
								flex: 1,
								overflow: 'auto',
							}}
							data-testid="master-layout-new-layout-main"
						>
							{children}
						</Box>
					</Box>
				</Box>
			</Box>
			{shouldShowAIAssistant && <AIAssistantDialog />}
		</>
	);
};

export default NewLayout;
